import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/admin/AdminAuth";

const STATUSES = ["New", "Reviewing", "Approved", "Waitlist", "Declined"];
const AGREEMENTS = ["Not Sent", "Sent", "Accepted", "Declined"];
const PLATFORMS = ["TikTok / BookTok", "Instagram / Bookstagram", "Facebook", "Goodreads", "Amazon", "YouTube", "Blog / Website", "Other"];
const GENRES = ["Urban Fiction", "Black Fiction", "Crime / Suspense", "Mystery", "Romance", "Family Drama", "Psychological Fiction", "Other"];
const FOLLOWER_RANGES = [
  ["under_1000", "Under 1,000"], ["1000_4999", "1,000–4,999"], ["5000_9999", "5,000–9,999"],
  ["10000_24999", "10,000–24,999"], ["25000_49999", "25,000–49,999"], ["50000_plus", "50,000+"],
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

// Renders full URLs as new-tab links; plain usernames/handles stay as text.
function Linkified({ text }) {
  if (!text) return <span>—</span>;
  const parts = String(text).split(/(\s+)/);
  return (
    <span className="whitespace-pre-wrap break-words">
      {parts.map((p, i) =>
        /^https?:\/\/\S+$/i.test(p) ? (
          <a key={i} data-testid="arc-profile-url" href={p} target="_blank" rel="noopener noreferrer" className="text-dh-gold underline underline-offset-4 transition-colors hover:text-dh-cream">{p}</a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </span>
  );
}

export default function AdminArc() {
  const { authHeader, logout } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [platform, setPlatform] = useState("");
  const [genre, setGenre] = useState("");
  const [followerRange, setFollowerRange] = useState("");
  const [selected, setSelected] = useState(null);
  const [agreementMeta, setAgreementMeta] = useState({ review_window: "", embargo_date: "" });
  const [acceptance, setAcceptance] = useState(null);
  const [agreementBusy, setAgreementBusy] = useState(false);
  const [pendingDecline, setPendingDecline] = useState(false);
  const [pendingWaitlist, setPendingWaitlist] = useState(false);
  const [confirmReminder, setConfirmReminder] = useState(false);
  const [confirmSpotOpened, setConfirmSpotOpened] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState(null); // "Waitlist" | "Declined"
  const [bulkBusy, setBulkBusy] = useState(false);
  const [printFilter, setPrintFilter] = useState("");
  const [shippingAddr, setShippingAddr] = useState(null);
  const [confirmOfferDigital, setConfirmOfferDigital] = useState(false);
  const [shipment, setShipment] = useState({ carrier: "", tracking_number: "", shipment_date: "", shipping_note: "" });

  const printAction = async (id, path) => {
    setAgreementBusy(true);
    try {
      const res = await api.post(`/admin/arc-applications/${id}/${path}`, {}, { headers: authHeader() });
      setSelected(res.data);
      if (path === "print/request-address") {
        if (res.data.address_email_status === "Failed") toast.error("Address request saved, but the email failed. You can request again.");
        else toast.success("Shipping address request sent.");
      } else if (path === "print/ship-notice/resend") {
        if (res.data.ship_notice_status === "Failed") toast.error("Ship notice failed. Error recorded — you can retry.");
        else toast.success("Ship notice resent.");
      } else if (path === "print/delivery-notice/resend") {
        if (res.data.delivery_notice_status === "Failed") toast.error("Delivery notice failed. Error recorded — you can retry.");
        else toast.success("Delivery notice resent.");
      } else if (res.data.digital_offer_email_status === "Failed") {
        toast.error("Digital offer saved, but the email failed. You can retry.");
      } else toast.success("Digital offer sent.");
      setConfirmOfferDigital(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action failed.");
    } finally { setAgreementBusy(false); }
  };

  const viewShippingAddress = async (id) => {
    try {
      const res = await api.get(`/admin/arc-applications/${id}/shipping-address`, { headers: authHeader() });
      setShippingAddr(res.data);
    } catch { toast.error("No shipping address on file."); }
  };

  const toggleSelect = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const runBulk = async () => {
    const action = bulkAction;
    setBulkBusy(true);
    let ok = 0, failed = 0;
    for (const id of selectedIds) {
      try {
        await api.patch(`/admin/arc-applications/${id}`,
          { status: action, send_waitlist_email: action === "Waitlist", send_denial_email: action === "Declined" },
          { headers: authHeader() });
        ok += 1;
      } catch { failed += 1; }
    }
    setBulkBusy(false);
    setBulkAction(null);
    setSelectedIds([]);
    load();
    if (failed) toast.error(`${ok} updated, ${failed} failed.`);
    else toast.success(`${ok} applicant${ok === 1 ? "" : "s"} moved to ${action} and notified.`);
  };

  const sendSpotOpened = async (id) => {
    setAgreementBusy(true);
    try {
      const res = await api.post(`/admin/arc-applications/${id}/spot-opened-email`, {}, { headers: authHeader() });
      setSelected(res.data);
      if (res.data.spot_opened_email_status === "Failed") toast.error("Spot-opened email failed. Error recorded — you can retry.");
      else toast.success("Spot-opened email sent.");
      setConfirmSpotOpened(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Send failed.");
    } finally { setAgreementBusy(false); }
  };

  const sendDigestNow = async () => {
    try {
      const res = await api.post("/admin/arc-digest/send-now", {}, { headers: authHeader() });
      const s = res.data.summary;
      toast.success(`Digest sent — ${s.new_count} new, ${s.pending_agreements} pending agreements, ${s.delivered} delivered this week.`);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Digest could not be sent.");
    }
  };

  const resendWaitlist = async (id) => {
    setAgreementBusy(true);
    try {
      const res = await api.post(`/admin/arc-applications/${id}/waitlist-email/resend`, {}, { headers: authHeader() });
      setSelected(res.data);
      if (res.data.waitlist_email_status === "Failed") toast.error("Waitlist email failed again. Error recorded for review.");
      else toast.success("Waitlist email resent.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Resend failed.");
    } finally { setAgreementBusy(false); }
  };

  const sendReminder = async (id) => {
    setAgreementBusy(true);
    try {
      const res = await api.post(`/admin/arc-applications/${id}/agreement/remind`, {}, { headers: authHeader() });
      setSelected(res.data);
      if (res.data.latest_reminder_email_status === "Failed") toast.error("Reminder recorded but the email failed. You can retry.");
      else toast.success("Agreement reminder sent.");
      setConfirmReminder(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reminder failed.");
    } finally { setAgreementBusy(false); }
  };

  const resendDenial = async (id) => {
    setAgreementBusy(true);
    try {
      const res = await api.post(`/admin/arc-applications/${id}/denial-email/resend`, {}, { headers: authHeader() });
      setSelected(res.data);
      if (res.data.denial_email_status === "Failed") toast.error("Denial email failed again. Error recorded for review.");
      else toast.success("Denial email resent.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Resend failed.");
    } finally { setAgreementBusy(false); }
  };

  const agreementAction = async (id, action, body = {}) => {
    setAgreementBusy(true);
    try {
      const res = await api.post(`/admin/arc-applications/${id}/agreement/${action}`, body, { headers: authHeader() });
      setSelected(res.data);
      if (action === "send") {
        if (res.data.agreement_email_status === "failed") toast.error("Agreement saved, but the email failed. You can resend safely.");
        else toast.success("Agreement sent to reviewer.");
      } else {
        toast.success("Agreement link revoked.");
      }
      setAcceptance(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Agreement action failed.");
    } finally { setAgreementBusy(false); }
  };

  const viewAcceptance = async (id) => {
    try {
      const res = await api.get(`/admin/arc-applications/${id}/agreement/acceptance`, { headers: authHeader() });
      setAcceptance(res.data);
    } catch { toast.error("No acceptance record found."); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (platform) params.platform = platform;
      if (genre) params.genre = genre;
      if (followerRange) params.follower_range = followerRange;
      if (printFilter) params.print_filter = printFilter;
      const res = await api.get("/admin/arc-applications", { headers: authHeader(), params });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Could not load ARC applications.");
    } finally { setLoading(false); }
  }, [q, status, platform, genre, followerRange, printFilter, authHeader, logout]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/admin/arc-applications/${id}`, { headers: authHeader() });
      setSelected(res.data);
      setAcceptance(null);
      setShippingAddr(null);
      setConfirmOfferDigital(false);
      setShipment({
        carrier: res.data.carrier || "", tracking_number: res.data.tracking_number || "",
        shipment_date: res.data.shipment_date || "", shipping_note: res.data.shipping_note || "",
      });
      setAgreementMeta({ review_window: res.data.agreement_review_window || "", embargo_date: res.data.agreement_embargo_date || "" });
    } catch { toast.error("Could not open application."); }
  };

  const patch = async (id, body) => {
    try {
      const res = await api.patch(`/admin/arc-applications/${id}`, body, { headers: authHeader() });
      setSelected(res.data);
      toast.success("Updated.");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/arc-applications.csv", { headers: authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `dhamma-arc-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch { toast.error("Export failed."); }
  };

  const s = data?.stats || {};

  return (
    <div data-testid="admin-arc">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-6">
        {["total", ...STATUSES].map((k) => (
          <div key={k} className="border border-dh-cream/10 bg-dh-bg2 p-5">
            <p className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">{k === "total" ? "Total" : k}</p>
            <p className="mt-2 font-display text-3xl text-dh-cream">{s[k === "total" ? "total" : k] ?? "—"}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 border border-dh-cream/15 px-3">
          <Search size={14} className="text-dh-muted" />
          <input data-testid="arc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, copy ID…" className="bg-transparent py-2.5 font-body text-sm text-dh-cream outline-none placeholder:text-dh-muted/60" />
        </div>
        <select data-testid="arc-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-dh-cream/15 bg-dh-bg2 px-3 py-2.5 font-body text-sm text-dh-cream outline-none">
          <option value="">All statuses</option>
          {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
        <select data-testid="arc-filter-platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className="border border-dh-cream/15 bg-dh-bg2 px-3 py-2.5 font-body text-sm text-dh-cream outline-none">
          <option value="">All platforms</option>
          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="arc-filter-genre" value={genre} onChange={(e) => setGenre(e.target.value)} className="border border-dh-cream/15 bg-dh-bg2 px-3 py-2.5 font-body text-sm text-dh-cream outline-none">
          <option value="">All genres</option>
          {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select data-testid="arc-filter-followers" value={followerRange} onChange={(e) => setFollowerRange(e.target.value)} className="border border-dh-cream/15 bg-dh-bg2 px-3 py-2.5 font-body text-sm text-dh-cream outline-none">
          <option value="">All follower counts</option>
          {FOLLOWER_RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="arc-filter-print" value={printFilter} onChange={(e) => setPrintFilter(e.target.value)} className="border border-dh-cream/15 bg-dh-bg2 px-3 py-2.5 font-body text-sm text-dh-cream outline-none">
          <option value="">All formats</option>
          <option value="requested_print">Requested Print</option>
          <option value="approved_print">Approved Print</option>
          <option value="approved_digital">Approved Digital</option>
          {["Address Requested", "Address Received", "Preparing Package", "Shipped", "Delivered"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button onClick={sendDigestNow} data-testid="arc-send-digest" className="ml-auto inline-flex items-center gap-2 border border-dh-cream/25 px-4 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream">
          Send Digest Now
        </button>
        <button onClick={exportCsv} data-testid="arc-export-csv" className="inline-flex items-center gap-2 border border-dh-gold/60 px-4 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="mt-6 overflow-x-auto border border-dh-cream/10">
        {selectedIds.length > 0 && (
          <div data-testid="arc-bulk-bar" className="flex flex-wrap items-center gap-4 border-b border-dh-gold/25 bg-dh-bg2/60 px-4 py-3">
            <span className="font-body text-xs text-dh-cream">{selectedIds.length} selected</span>
            <button data-testid="arc-bulk-waitlist" disabled={bulkBusy} onClick={() => setBulkAction("Waitlist")} className="border border-dh-gold/70 px-4 py-1.5 font-body text-[10px] uppercase tracking-[0.14em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
              Waitlist Selected
            </button>
            <button data-testid="arc-bulk-decline" disabled={bulkBusy} onClick={() => setBulkAction("Declined")} className="border border-red-400/50 px-4 py-1.5 font-body text-[10px] uppercase tracking-[0.14em] text-red-300 transition-colors hover:bg-red-400/10 disabled:opacity-40">
              Decline Selected
            </button>
            <button data-testid="arc-bulk-clear" onClick={() => setSelectedIds([])} className="font-body text-[10px] uppercase tracking-[0.14em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
              Clear
            </button>
          </div>
        )}
        {bulkAction && (
          <div data-testid="arc-bulk-confirm-dialog" className="border-b border-dh-gold/40 bg-dh-bg2/80 px-4 py-4">
            <p className="font-body text-sm text-dh-cream">
              {bulkAction === "Waitlist"
                ? `Waitlist ${selectedIds.length} applicant${selectedIds.length === 1 ? "" : "s"} and send each a notification email?`
                : `Decline ${selectedIds.length} application${selectedIds.length === 1 ? "" : "s"} and send each applicant a notification email?`}
            </p>
            <div className="mt-3 flex items-center gap-4">
              <button data-testid="arc-bulk-confirm-send" disabled={bulkBusy} onClick={runBulk} className="border border-dh-gold/70 px-5 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                {bulkBusy ? "Working…" : bulkAction === "Waitlist" ? "Waitlist & Send Emails" : "Decline & Send Emails"}
              </button>
              <button data-testid="arc-bulk-cancel" disabled={bulkBusy} onClick={() => setBulkAction(null)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
                Cancel
              </button>
            </div>
          </div>
        )}
        <table className="w-full min-w-[900px] border-collapse" data-testid="arc-table">
          <thead>
            <tr className="border-b border-dh-cream/10 bg-dh-bg2 text-left">
              <th className="w-10 p-3">
                <input type="checkbox" data-testid="arc-select-all"
                  checked={Boolean(data?.applications?.length) && selectedIds.length === data.applications.length}
                  onChange={(e) => setSelectedIds(e.target.checked ? data.applications.map((a) => a.id) : [])}
                  className="h-4 w-4 accent-dh-gold" />
              </th>
              {["Name", "Email", "Platforms", "Followers", "Format", "Submitted", "Status", "Agreement", "Copy ID"].map((h) => (
                <th key={h} className="p-3 font-body text-[10px] uppercase tracking-[0.14em] text-dh-muted">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="p-8 text-center font-body text-sm text-dh-muted">Loading…</td></tr>
            ) : data?.applications?.length ? data.applications.map((a) => (
              <tr key={a.id} data-testid="arc-row" onClick={() => openDetail(a.id)} className="cursor-pointer border-b border-dh-cream/5 hover:bg-dh-bg2/50">
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" data-testid="arc-row-select" checked={selectedIds.includes(a.id)} onChange={() => toggleSelect(a.id)} className="h-4 w-4 accent-dh-gold" />
                </td>
                <td className="p-3 font-body text-sm text-dh-cream">{a.full_name}</td>
                <td className="p-3 font-body text-xs text-dh-muted">{a.email}</td>
                <td className="p-3 font-body text-xs text-dh-muted">{(a.review_platforms || []).join(", ") || "—"}</td>
                <td className="p-3 font-body text-xs text-dh-muted">{a.follower_count || "—"}</td>
                <td className="p-3 font-body text-xs text-dh-muted">{a.preferred_arc_format || "—"}</td>
                <td className="p-3 font-body text-xs text-dh-muted">{fmt(a.submitted_at)}</td>
                <td className="p-3"><span className="border border-dh-gold/40 px-2 py-1 font-body text-[10px] uppercase tracking-[0.12em] text-dh-gold">{a.status}</span></td>
                <td className="p-3">{a.agreement_status === "Accepted" ? <span data-testid="arc-accepted-badge" className="border border-dh-gold/60 bg-dh-gold/10 px-2 py-1 font-body text-[10px] uppercase tracking-[0.12em] text-dh-gold">Accepted</span> : <span className="font-body text-xs text-dh-muted">{a.agreement_status}</span>}</td>
                <td className="p-3 font-body text-xs text-dh-cream">{a.assigned_copy_id || "—"}</td>
              </tr>
            )) : (
              <tr><td colSpan={10} className="p-8 text-center font-body text-sm text-dh-muted">No applications yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8" onClick={() => setSelected(null)}>
          <div data-testid="arc-detail" className="my-8 w-full max-w-3xl border border-dh-gold/25 bg-dh-bg2 p-6 sm:p-9" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-3xl text-dh-cream">{selected.full_name}</h3>
                <p className="mt-1 font-body text-sm text-dh-muted">{selected.email}</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="arc-detail-close" className="text-dh-muted hover:text-dh-cream"><X size={22} /></button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Status</span>
                <select data-testid="arc-detail-status" value={selected.status} onChange={(e) => {
                  if (e.target.value === "Declined" && selected.status !== "Declined") setPendingDecline(true);
                  else if (e.target.value === "Waitlist" && selected.status !== "Waitlist") setPendingWaitlist(true);
                  else patch(selected.id, { status: e.target.value });
                }} className="mt-2 w-full border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream">
                  {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Agreement</span>
                <select data-testid="arc-detail-agreement" value={selected.agreement_status} onChange={(e) => patch(selected.id, { agreement_status: e.target.value })} className="mt-2 w-full border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream">
                  {AGREEMENTS.map((ag) => <option key={ag} value={ag}>{ag}</option>)}
                </select>
              </label>
              <div>
                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Copy ID</span>
                <div className="mt-2 flex items-center gap-2">
                  <span className="font-body text-sm text-dh-cream">{selected.assigned_copy_id || "—"}</span>
                  {!selected.assigned_copy_id && (
                    <button data-testid="arc-assign-copy" onClick={() => patch(selected.id, { assign_next_copy_id: true })} className="border border-dh-gold/50 px-2 py-1 font-body text-[10px] uppercase tracking-[0.12em] text-dh-gold hover:bg-dh-gold hover:text-dh-bg2">Assign next</button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div>
                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Requested Format</span>
                <p className="mt-2 font-body text-sm text-dh-cream" data-testid="arc-requested-format">
                  {selected.preferred_arc_format === "Digital PDF" ? "Digital" : selected.preferred_arc_format === "Either" ? "No Preference" : (selected.preferred_arc_format || "—")}
                </p>
              </div>
              <label className="block">
                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Approved Format</span>
                <select data-testid="arc-approved-format" value={selected.approved_format || ""} onChange={(e) => patch(selected.id, { approved_format: e.target.value })} className="mt-2 w-full border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream">
                  <option value="">Not Yet Assigned</option>
                  <option value="Digital">Digital</option>
                  <option value="Print">Print</option>
                </select>
              </label>
              <div>
                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Digital Offer</span>
                <p className="mt-2 font-body text-sm text-dh-cream" data-testid="arc-digital-offer-status">{selected.digital_offer_status || "—"}</p>
              </div>
            </div>

            {selected.preferred_arc_format === "Print" && selected.status !== "Declined" && selected.approved_format !== "Digital" && (
              <div className="mt-4">
                <button data-testid="arc-offer-digital" disabled={agreementBusy} onClick={() => setConfirmOfferDigital(true)} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                  Offer Digital Instead
                </button>
                {confirmOfferDigital && (
                  <div data-testid="arc-offer-digital-dialog" className="mt-3 border border-dh-gold/40 bg-dh-bg2/70 p-5">
                    <p className="font-body text-sm text-dh-cream">Email this applicant a Digital ARC offer instead of Print?</p>
                    <p className="mt-1 font-body text-xs text-dh-muted">This does not decline their application. They can accept or decline via a secure link.</p>
                    <div className="mt-4 flex items-center gap-4">
                      <button data-testid="arc-offer-digital-send" disabled={agreementBusy} onClick={() => printAction(selected.id, "offer-digital")} className="border border-dh-gold/70 px-5 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                        Send Digital Offer
                      </button>
                      <button data-testid="arc-offer-digital-cancel" onClick={() => setConfirmOfferDigital(false)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selected.approved_format === "Print" && (
              <div className="mt-6 border border-dh-gold/25 bg-dh-forest/10 p-5" data-testid="arc-print-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Print Fulfillment</span>
                  <span data-testid="arc-print-status" className="border border-dh-gold/60 bg-dh-gold/10 px-2 py-1 font-body text-[10px] uppercase tracking-[0.12em] text-dh-gold">{selected.print_status || "Not Started"}</span>
                </div>
                {(selected.print_history || []).length > 0 && (
                  <p className="mt-2 font-body text-xs text-dh-muted" data-testid="arc-print-history">
                    {selected.print_history.map((h) => `${h.status} · ${fmt(h.at)}`).join("  →  ")}
                  </p>
                )}
                {!(selected.status === "Approved" && selected.agreement_status === "Accepted") && (
                  <p className="mt-3 font-body text-xs text-dh-muted">Address collection unlocks once the applicant is Approved and their reviewer agreement is Accepted.</p>
                )}
                {selected.status === "Approved" && selected.agreement_status === "Accepted" && (
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button data-testid="arc-request-address" disabled={agreementBusy} onClick={() => printAction(selected.id, "print/request-address")} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                      {selected.address_requested_at ? "Re-request Shipping Address" : "Request Shipping Address"}
                    </button>
                    {selected.address_requested_at && (
                      <span className="font-body text-xs text-dh-muted">requested {fmt(selected.address_requested_at)} · email {selected.address_email_status || "—"}</span>
                    )}
                  </div>
                )}
                {["Address Received", "Preparing Package", "Shipped", "Delivered"].includes(selected.print_status) && (
                  <div className="mt-4 border-t border-dh-cream/10 pt-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <button data-testid="arc-view-address" onClick={() => viewShippingAddress(selected.id)} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream">
                        View Shipping Address
                      </button>
                      <button data-testid="arc-address-label" onClick={async () => {
                        try {
                          const res = await api.get(`/admin/arc-applications/${selected.id}/shipping-label.pdf`, { headers: authHeader(), responseType: "blob" });
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                          const a = document.createElement("a");
                          a.href = url; a.download = `dhamma-print-arc-label-${selected.assigned_copy_id || "arc"}.pdf`;
                          document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                          toast.success("Address label downloaded.");
                        } catch { toast.error("Label download failed."); }
                      }} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream">
                        Download Address Label
                      </button>
                      {selected.print_status !== "Shipped" && selected.print_status !== "Delivered" && (
                        <button data-testid="arc-preparing-package" onClick={() => patch(selected.id, { print_status: "Preparing Package" })} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream">
                          Preparing Package
                        </button>
                      )}
                      {["Address Received", "Preparing Package"].includes(selected.print_status) && (
                        <button data-testid="arc-mark-shipped" onClick={() => patch(selected.id, { mark_shipped: true, carrier: shipment.carrier, tracking_number: shipment.tracking_number, shipment_date: shipment.shipment_date, shipping_note: shipment.shipping_note })} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2">
                          Mark as Shipped
                        </button>
                      )}
                      {selected.print_status === "Shipped" && (
                        <button data-testid="arc-mark-print-delivered" onClick={() => patch(selected.id, { mark_print_delivered: true })} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2">
                          Mark as Delivered
                        </button>
                      )}
                    </div>
                    {shippingAddr && (
                      <div data-testid="arc-shipping-address" className="mt-4 border border-dh-cream/10 bg-dh-bg/40 p-4 font-body text-sm leading-relaxed text-dh-cream/85">
                        <p className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Shipping Address (private)</p>
                        <p className="mt-2">{shippingAddr.recipient_name}<br />{shippingAddr.line1}{shippingAddr.line2 ? <><br />{shippingAddr.line2}</> : null}<br />{shippingAddr.city}, {shippingAddr.state} {shippingAddr.postal_code}<br />{shippingAddr.country}</p>
                        <p className="mt-2 font-body text-xs text-dh-muted">Submitted {fmt(shippingAddr.submitted_at)}</p>
                      </div>
                    )}
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <input data-testid="arc-carrier" value={shipment.carrier} onChange={(e) => setShipment((s) => ({ ...s, carrier: e.target.value }))} onBlur={() => patch(selected.id, { carrier: shipment.carrier })} placeholder="Carrier (e.g. USPS, UPS)" className="border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" />
                      <input data-testid="arc-tracking" value={shipment.tracking_number} onChange={(e) => setShipment((s) => ({ ...s, tracking_number: e.target.value }))} onBlur={() => patch(selected.id, { tracking_number: shipment.tracking_number })} placeholder="Tracking number (optional)" className="border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" />
                      <input data-testid="arc-shipment-date" value={shipment.shipment_date} onChange={(e) => setShipment((s) => ({ ...s, shipment_date: e.target.value }))} onBlur={() => patch(selected.id, { shipment_date: shipment.shipment_date })} placeholder="Shipment date (e.g. 2026-10-20)" className="border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" />
                      <input data-testid="arc-shipping-note" value={shipment.shipping_note} onChange={(e) => setShipment((s) => ({ ...s, shipping_note: e.target.value }))} onBlur={() => patch(selected.id, { shipping_note: shipment.shipping_note })} placeholder="Internal shipping note (optional)" className="border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" />
                    </div>
                    {(selected.shipped_at || selected.print_delivered_at) && (
                      <p className="mt-3 font-body text-xs text-dh-muted">
                        {selected.shipped_at ? <>Shipped {fmt(selected.shipped_at)}{selected.carrier ? ` via ${selected.carrier}` : ""}{selected.tracking_number ? ` · tracking ${selected.tracking_number}` : " · no tracking"}. </> : null}
                        {selected.print_delivered_at ? <>Marked delivered {fmt(selected.print_delivered_at)} (admin-recorded, not carrier-confirmed).</> : null}
                      </p>
                    )}
                    {["Shipped", "Delivered"].includes(selected.print_status) && (
                      <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="arc-ship-notice-row">
                        <span className="font-body text-xs text-dh-muted">
                          Ship notice: <span className={selected.ship_notice_status === "Failed" ? "text-red-300" : "text-dh-cream"} data-testid="arc-ship-notice-status">{selected.ship_notice_status || "Not Sent"}</span>
                          {selected.ship_notice_sent_at ? <> · sent {fmt(selected.ship_notice_sent_at)}</> : null}
                        </span>
                        <button data-testid="arc-resend-ship-notice" disabled={agreementBusy} onClick={() => printAction(selected.id, "print/ship-notice/resend")} className="border border-dh-cream/25 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.14em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                          Resend Ship Notice
                        </button>
                      </div>
                    )}
                    {selected.print_status === "Delivered" && (
                      <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="arc-delivery-notice-row">
                        <span className="font-body text-xs text-dh-muted">
                          Delivery notice: <span className={selected.delivery_notice_status === "Failed" ? "text-red-300" : "text-dh-cream"} data-testid="arc-delivery-notice-status">{selected.delivery_notice_status || "Not Sent"}</span>
                          {selected.delivery_notice_sent_at ? <> · sent {fmt(selected.delivery_notice_sent_at)}</> : null}
                        </span>
                        <button data-testid="arc-resend-delivery-notice" disabled={agreementBusy} onClick={() => printAction(selected.id, "print/delivery-notice/resend")} className="border border-dh-cream/25 px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.14em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                          Resend Delivery Notice
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {pendingDecline && (
              <div data-testid="arc-decline-confirm-dialog" className="mt-4 border border-dh-gold/40 bg-dh-bg2/70 p-5">
                <p className="font-body text-sm text-dh-cream">Decline this ARC application and send the applicant a notification email?</p>
                <p className="mt-1 font-body text-xs text-dh-muted">The email is polite and generic — it never includes internal notes or a specific reason.</p>
                <div className="mt-4 flex items-center gap-4">
                  <button data-testid="arc-decline-confirm-send" disabled={agreementBusy} onClick={async () => { setPendingDecline(false); await patch(selected.id, { status: "Declined", send_denial_email: true }); }} className="border border-dh-gold/70 px-5 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                    Decline & Send Email
                  </button>
                  <button data-testid="arc-decline-cancel" onClick={() => setPendingDecline(false)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {selected.status === "Declined" && (
              <div data-testid="arc-denial-info" className="mt-4 border border-dh-cream/10 bg-dh-bg/40 p-5">                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Application Decline</span>
                <p className="mt-2 font-body text-xs text-dh-muted">
                  Declined: <span className="text-dh-cream">{fmt(selected.declined_at)}</span>
                  {selected.declined_by ? <> · by <span className="text-dh-cream">{selected.declined_by}</span></> : null}
                  {" "}· Denial email: <span className={selected.denial_email_status === "Failed" ? "text-red-300" : "text-dh-cream"} data-testid="arc-denial-email-status">{selected.denial_email_status || "Not Sent"}</span>
                  {selected.denial_email_sent_at ? <> · sent {fmt(selected.denial_email_sent_at)}</> : null}
                </p>
                {selected.denial_email_error && (
                  <p className="mt-2 font-body text-xs text-red-300/80" data-testid="arc-denial-email-error">Delivery error: {selected.denial_email_error}</p>
                )}
                <button data-testid="arc-resend-denial" disabled={agreementBusy} onClick={() => resendDenial(selected.id)} className="mt-3 border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                  Resend Denial Email
                </button>
              </div>
            )}

            {pendingWaitlist && (
              <div data-testid="arc-waitlist-confirm-dialog" className="mt-4 border border-dh-gold/40 bg-dh-bg2/70 p-5">
                <p className="font-body text-sm text-dh-cream">Waitlist this applicant and send them a notification email?</p>
                <p className="mt-1 font-body text-xs text-dh-muted">The email politely lets them know they're on the waitlist and that you'll reach out if a spot opens.</p>
                <div className="mt-4 flex items-center gap-4">
                  <button data-testid="arc-waitlist-confirm-send" disabled={agreementBusy} onClick={async () => { setPendingWaitlist(false); await patch(selected.id, { status: "Waitlist", send_waitlist_email: true }); }} className="border border-dh-gold/70 px-5 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                    Waitlist & Send Email
                  </button>
                  <button data-testid="arc-waitlist-cancel" onClick={() => setPendingWaitlist(false)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {selected.status === "Waitlist" && (
              <div data-testid="arc-waitlist-info" className="mt-4 border border-dh-cream/10 bg-dh-bg/40 p-5">
                <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Waitlist</span>
                <p className="mt-2 font-body text-xs text-dh-muted">
                  Waitlisted: <span className="text-dh-cream">{fmt(selected.waitlisted_at)}</span>
                  {selected.waitlisted_by ? <> · by <span className="text-dh-cream">{selected.waitlisted_by}</span></> : null}
                  {" "}· Waitlist email: <span className={selected.waitlist_email_status === "Failed" ? "text-red-300" : "text-dh-cream"} data-testid="arc-waitlist-email-status">{selected.waitlist_email_status || "Not Sent"}</span>
                  {selected.waitlist_email_sent_at ? <> · sent {fmt(selected.waitlist_email_sent_at)}</> : null}
                </p>
                {selected.waitlist_email_error && (
                  <p className="mt-2 font-body text-xs text-red-300/80" data-testid="arc-waitlist-email-error">Delivery error: {selected.waitlist_email_error}</p>
                )}
                {selected.spot_opened_email_status && (
                  <p className="mt-2 font-body text-xs text-dh-muted" data-testid="arc-spot-opened-info">
                    Spot-opened email: <span className={selected.spot_opened_email_status === "Failed" ? "text-red-300" : "text-dh-cream"}>{selected.spot_opened_email_status}</span>
                    {selected.spot_opened_email_sent_at ? <> · sent {fmt(selected.spot_opened_email_sent_at)}</> : null}
                    {selected.spot_opened_notified_by ? <> · by {selected.spot_opened_notified_by}</> : null}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button data-testid="arc-resend-waitlist" disabled={agreementBusy} onClick={() => resendWaitlist(selected.id)} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                    Resend Waitlist Email
                  </button>
                  <button data-testid="arc-spot-opened" disabled={agreementBusy} onClick={() => setConfirmSpotOpened(true)} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                    Send Spot-Opened Email
                  </button>
                </div>
                {confirmSpotOpened && (
                  <div data-testid="arc-spot-opened-confirm-dialog" className="mt-4 border border-dh-gold/40 bg-dh-bg2/70 p-5">
                    <p className="font-body text-sm text-dh-cream">Let this waitlisted applicant know a spot has opened on the ARC team?</p>
                    <p className="mt-1 font-body text-xs text-dh-muted">They'll be told no action is needed yet — approve them with a copy ID when you're ready to send their agreement.</p>
                    <div className="mt-4 flex items-center gap-4">
                      <button data-testid="arc-spot-opened-confirm-send" disabled={agreementBusy} onClick={() => sendSpotOpened(selected.id)} className="border border-dh-gold/70 px-5 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                        Send Spot-Opened Email
                      </button>
                      <button data-testid="arc-spot-opened-cancel" onClick={() => setConfirmSpotOpened(false)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {(selected.status !== "Approved" || !selected.assigned_copy_id) && (
              <div className="mt-5">
                <button data-testid="arc-approve-assign" onClick={() => patch(selected.id, { approve_with_copy_id: true })} className="inline-flex items-center gap-2 border border-dh-gold/70 px-5 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2">
                  Approve + Assign Copy ID
                </button>
                <p className="mt-2 font-body text-xs text-dh-muted">Approves the applicant and assigns the next sequential copy ID in one step. Existing IDs are never reused.</p>
              </div>
            )}

            <div className="mt-6 border border-dh-cream/10 bg-dh-bg/40 p-5" data-testid="arc-agreement-panel">
              <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Reviewer Agreement</span>
              <p className="mt-2 font-body text-xs text-dh-muted">
                Status: <span className="text-dh-cream">{selected.agreement_status}</span>
                {selected.agreement_version ? <> · Version: <span className="text-dh-cream">{selected.agreement_version}</span></> : null}
                {selected.agreement_sent_at ? <> · Sent: <span className="text-dh-cream">{fmt(selected.agreement_sent_at)}</span></> : null}
                {selected.agreement_accepted_at ? <> · Accepted: <span className="text-dh-gold">{fmt(selected.agreement_accepted_at)}</span></> : null}
                {selected.agreement_declined_at ? <> · Declined: <span className="text-dh-cream">{fmt(selected.agreement_declined_at)}</span></> : null}
              </p>
              {selected.agreement_status === "Sent" && (
                <p className="mt-2 font-body text-xs text-dh-muted" data-testid="arc-reminder-info">
                  Reminders sent: <span className="text-dh-cream">{selected.reminder_count || 0}</span>
                  {selected.reminder_sent_at ? <> · Last: <span className="text-dh-cream">{fmt(selected.reminder_sent_at)}</span></> : null}
                  {selected.latest_reminder_email_status ? <> · Latest email: <span className={selected.latest_reminder_email_status === "Failed" ? "text-red-300" : "text-dh-cream"}>{selected.latest_reminder_email_status}</span></> : null}
                </p>
              )}
              {(selected.agreement_status === "Not Sent" || selected.agreement_status === "Declined" || selected.agreement_status === "Sent") && selected.status === "Approved" && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <input data-testid="arc-review-window" value={agreementMeta.review_window} onChange={(e) => setAgreementMeta((s) => ({ ...s, review_window: e.target.value }))} placeholder="Review window (optional)" className="border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" />
                  <input data-testid="arc-embargo-date" value={agreementMeta.embargo_date} onChange={(e) => setAgreementMeta((s) => ({ ...s, embargo_date: e.target.value }))} placeholder="Embargo date (optional)" className="border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" />
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {(selected.agreement_status === "Not Sent" || selected.agreement_status === "Declined") && (
                  <button data-testid="arc-send-agreement" disabled={agreementBusy || selected.status !== "Approved" || !selected.assigned_copy_id} onClick={() => agreementAction(selected.id, "send", { review_window: agreementMeta.review_window || null, embargo_date: agreementMeta.embargo_date || null })} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:cursor-not-allowed disabled:opacity-40">
                    Send Reviewer Agreement
                  </button>
                )}
                {selected.agreement_status === "Sent" && (
                  <>
                    <button data-testid="arc-resend-agreement" disabled={agreementBusy || selected.status !== "Approved"} onClick={() => agreementAction(selected.id, "send", { review_window: agreementMeta.review_window || null, embargo_date: agreementMeta.embargo_date || null })} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:cursor-not-allowed disabled:opacity-40">
                      Resend Agreement
                    </button>
                    <button data-testid="arc-send-reminder" disabled={agreementBusy || selected.status !== "Approved"} onClick={() => setConfirmReminder(true)} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                      Send Agreement Reminder
                    </button>
                    <button data-testid="arc-revoke-agreement" disabled={agreementBusy} onClick={() => agreementAction(selected.id, "revoke")} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream disabled:opacity-40">
                      Revoke Agreement Link
                    </button>
                  </>
                )}
                {selected.agreement_status === "Accepted" && (
                  <>
                    <button data-testid="arc-view-acceptance" onClick={() => viewAcceptance(selected.id)} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2">
                      View Acceptance Record
                    </button>
                    <button data-testid="arc-download-pdf" onClick={async () => {
                      try {
                        const res = await api.get(`/admin/arc-applications/${selected.id}/agreement/acceptance.pdf`, { headers: authHeader(), responseType: "blob" });
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        const a = document.createElement("a");
                        a.href = url; a.download = `dhamma-arc-agreement-${selected.assigned_copy_id || "record"}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                        toast.success("Acceptance record PDF downloaded.");
                      } catch { toast.error("PDF download failed."); }
                    }} className="border border-dh-cream/25 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-cream/70 transition-colors hover:border-dh-cream/60 hover:text-dh-cream">
                      Download PDF
                    </button>
                  </>
                )}
              </div>
              {confirmReminder && (
                <div data-testid="arc-reminder-confirm-dialog" className="mt-4 border border-dh-gold/40 bg-dh-bg2/70 p-5">
                  <p className="font-body text-sm text-dh-cream">Send a reminder that this reviewer's ARC Agreement is still waiting for acceptance?</p>
                  {selected.reminder_sent_at && (Date.now() - new Date(selected.reminder_sent_at).getTime()) < 24 * 3600 * 1000 && (
                    <p className="mt-2 font-body text-xs text-dh-gold" data-testid="arc-reminder-recent-warning">
                      A reminder was already sent recently ({fmt(selected.reminder_sent_at)}). Consider waiting before sending another.
                    </p>
                  )}
                  <div className="mt-4 flex items-center gap-4">
                    <button data-testid="arc-reminder-confirm-send" disabled={agreementBusy} onClick={() => sendReminder(selected.id)} className="border border-dh-gold/70 px-5 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40">
                      Send Reminder
                    </button>
                    <button data-testid="arc-reminder-cancel" onClick={() => setConfirmReminder(false)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {selected.status === "Approved" && !selected.assigned_copy_id && (
                <p className="mt-3 font-body text-xs text-dh-muted">Assign a copy ID before sending the agreement.</p>
              )}
              {acceptance && (
                <div data-testid="arc-acceptance-record" className="mt-5 space-y-2 border-t border-dh-cream/10 pt-4">
                  <p className="font-body text-[10px] uppercase tracking-ultra text-dh-gold">Acceptance Record (audit)</p>
                  {[
                    ["Legal name", acceptance.legal_name], ["Email", acceptance.email],
                    ["Copy ID", acceptance.assigned_copy_id], ["Version", acceptance.agreement_version],
                    ["Accepted at (UTC)", acceptance.accepted_at], ["IP address", acceptance.ip],
                    ["User agent", acceptance.user_agent], ["Review window", acceptance.review_window],
                    ["Embargo date", acceptance.embargo_date],
                    ["Text hash (SHA-256)", acceptance.agreement_text_sha256],
                  ].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 gap-3">
                      <span className="font-body text-[10px] uppercase tracking-[0.14em] text-dh-muted">{k}</span>
                      <span className="col-span-2 break-all font-body text-xs text-dh-cream/85">{v || "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5">
              <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">ARC Delivery</span>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {selected.arc_delivered_at ? (
                  <span data-testid="arc-delivered-at" className="border border-dh-gold/60 bg-dh-gold/10 px-2 py-1 font-body text-[10px] uppercase tracking-[0.12em] text-dh-gold">
                    Delivered · {fmt(selected.arc_delivered_at)}
                  </span>
                ) : (
                  <button data-testid="arc-mark-delivered" disabled={selected.status !== "Approved" || !selected.assigned_copy_id} onClick={() => patch(selected.id, { mark_delivered: true })} className="border border-dh-gold/70 px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:cursor-not-allowed disabled:opacity-40">
                    Mark ARC Delivered
                  </button>
                )}
                <input data-testid="arc-delivery-note" defaultValue={selected.arc_delivery_note || ""} placeholder="Delivery note (e.g. sent via email)" onBlur={(e) => { if (e.target.value !== (selected.arc_delivery_note || "")) patch(selected.id, { delivery_note: e.target.value }); }} className="min-w-[220px] flex-1 border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" />
              </div>
              <p className="mt-2 font-body text-xs text-dh-muted">Manual tracking only — ARC files are never sent by the system.</p>
            </div>

            <div className="mt-5">
              <span className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">Private admin notes</span>
              <textarea data-testid="arc-notes" defaultValue={selected.admin_notes || ""} rows={3} className="mt-2 w-full resize-none border border-dh-cream/15 bg-dh-bg px-3 py-2 font-body text-sm text-dh-cream outline-none focus:border-dh-gold" onBlur={(e) => { if (e.target.value !== (selected.admin_notes || "")) patch(selected.id, { admin_notes: e.target.value }); }} />
            </div>

            <div className="mt-6 space-y-3 border-t border-dh-cream/10 pt-6 text-sm">
              {[
                ["Preferred name", selected.preferred_name], ["Location", [selected.city, selected.state].filter(Boolean).join(", ")],
                ["Age", selected.age_confirmation], ["Social handles", selected.social_handles],
                ["Platforms", (selected.review_platforms || []).join(", ")], ["Profile links", selected.profile_links],
                ["Follower count", selected.follower_count], ["Posting frequency", selected.posting_frequency],
                ["Genres", (selected.genres || []).join(", ")], ["Recent books/authors", selected.recent_books_authors],
                ["Interest reason", selected.interest_reason], ["Reviewing style", selected.reviewing_style],
                ["Preferred format", selected.preferred_arc_format], ["Review window", selected.review_window_commitment],
                ["Avoid spoilers", selected.spoiler_agreement], ["Disclose free ARC", selected.disclosure_agreement],
                ["Accessibility needs", selected.accessibility_needs], ["Prior ARC experience", selected.prior_arc_experience],
                ["Additional notes", selected.additional_notes],
                ["Conduct acknowledged", `${(selected.conduct_acknowledgments || []).length} / 6`],
                ["Certification", selected.certification_accepted ? "Accepted" : "Not accepted"],
                ["Submitted", fmt(selected.submitted_at)], ["Approved", fmt(selected.approved_at)],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3">
                  <span className="font-body text-[11px] uppercase tracking-[0.14em] text-dh-muted">{k}</span>
                  {k === "Social handles" || k === "Profile links" ? (
                    <span className="col-span-2 font-body text-sm text-dh-cream/85"><Linkified text={v} /></span>
                  ) : (
                    <span className="col-span-2 whitespace-pre-wrap font-body text-sm text-dh-cream/85">{v || "—"}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
